import { ReactNode } from 'react';

import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';

import { Box } from '../../Box';
import { Label } from '../common';

import useStyles from './SwitchInput.styles';

/**
 * Свойства компонента {@link SwitchInput}
 */
interface SwitchInputProps {
  /**
   * Текст метки для переключателя.
   */
  label: string;

  /**
   * Иконка для метки.
   */
  labelIcon?: ReactNode;

  /**
   * Функция обратного вызова для обработки изменений состояния переключателя.
   */
  onChange: (check: boolean) => void;

  /**
   * Текущее состояние переключателя (включён/выключен).
   */
  checked: boolean;

  /**
   * Если true, переключатель будет отключён.
   */
  disabled?: boolean;

  /**
   * Положение метки относительно переключателя.
   */
  labelPlacement?: 'start' | 'end';

  /**
   * Содержимое всплывающей подсказки, отображаемой с меткой.
   */
  tooltip?: ReactNode | null;

  /**
   * Идентификатор для тестирования.
   */
  testId?: string;
}

/**
 * SwitchInput — это компонент, который предоставляет переключатель с меткой.
 * Он поддерживает настраиваемое положение метки, отключённое состояние и всплывающие подсказки.
 *
 * @public
 * @param label - Текст метки для переключателя.
 * @param labelIcon - Иконка для метки.
 * @param onChange - Функция обратного вызова для обработки изменений состояния переключателя.
 * @param checked - Текущее состояние переключателя (включён/выключен).
 * @param disabled - Если true, переключатель будет отключён.
 * @param labelPlacement - Положение метки относительно переключателя.
 * @param tooltip - Содержимое всплывающей подсказки, отображаемой с меткой.
 * @param testId - Необязательный идентификатор для автоматизированного тестирования.
 * @returns JSX компонента.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link SwitchInputProps}
 */
export const SwitchInput = ({
  label,
  labelIcon,
  onChange,
  disabled = false,
  labelPlacement = 'start',
  checked,
  tooltip,
  testId,
}: SwitchInputProps) => {
  const { classes } = useStyles();

  return (
    <FormControlLabel
      classes={{ root: classes.root }}
      control={
        <Switch
          checked={checked}
          disableRipple
          classes={{
            root: classes.switchRoot,
            track: classes.track,
            switchBase: classes.switchBase,
            disabled: classes.disabled,
            thumb: classes.thumb,
          }}
          color="primary"
          disabled={disabled}
          onChange={(event, check) => {
            onChange(check);
          }}
          data-testid={testId}
        />
      }
      label={
        label ? (
          <Box
            mr={labelPlacement === 'start' ? '16' : '0'}
            ml={labelPlacement === 'end' ? '16' : '0'}
            mb="-4"
          >
            <Label label={label} tooltip={tooltip} icon={labelIcon} />
          </Box>
        ) : null
      }
      labelPlacement={labelPlacement}
      className="aqa_switch_input"
    />
  );
};
